import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { LineChart, VerticalBarChart } from '../../../../../../shared/components/charts';
import { metricsApi } from '../../../../../../shared/api/metrics';
import { metricsQueryPolicy } from '../../../../../../shared/hooks/metricsQueryPolicy';
import { calculateGranularity } from '../../../shared/utils';
import { brandColors } from '../../../../../../theme/brandColors';
import type { SegmentComparisonBlock } from '../../api/types';

const PALETTE = [
  brandColors.peak4Blue,
  brandColors.summitGreen,
  brandColors.performOrange,
  brandColors.fuelIndigo,
  brandColors.recoverPink,
  brandColors.focusLilac,
];

interface SegmentComparisonViewProps {
  block: SegmentComparisonBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

export const SegmentComparisonView: React.FC<SegmentComparisonViewProps> = ({ block, clientId, fill }) => {
  const granularity = useMemo(
    () => calculateGranularity([dayjs(block.filters.start_date ?? null), dayjs(block.filters.end_date ?? null)]),
    [block.filters.start_date, block.filters.end_date],
  );

  const queries = useQueries({
    queries: block.segments.map((seg) => {
      const params = {
        ...block.filters,
        ...seg.filterOverride,
        client_id: clientId ?? undefined,
        metrics: [block.metric],
        granularity: block.granularity ?? granularity,
      };
      return {
        queryKey: ['reports', 'segmentComparison', block.id, seg.label, params],
        queryFn: ({ signal }) => metricsApi.getTrends(params, signal),
        ...metricsQueryPolicy,
        enabled: !!block.filters.start_date,
      };
    }),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.every((q) => q.isError);

  const chartData = useMemo(() => {
    // Build a map of period_start (ISO date) → display label from the first
    // query that has data, then sort by ISO date for correct chronological order.
    const periodMap = new Map<string, string>();
    queries.forEach((q) => {
      (q.data?.data?.trend ?? []).forEach((t) => {
        const iso = (t.period_start ?? t.label) as string;
        if (!periodMap.has(iso)) periodMap.set(iso, t.label as string);
      });
    });

    const sortedPeriods = Array.from(periodMap.keys()).sort();

    return sortedPeriods.map((iso) => {
      const label = periodMap.get(iso) as string;
      const point: Record<string, string | number | null> = { label };
      queries.forEach((q, i) => {
        const seg = block.segments[i];
        if (!seg) return;
        const row = (q.data?.data?.trend ?? []).find((t) => ((t.period_start ?? t.label) as string) === iso);
        const raw = row?.[block.metric];
        point[seg.label] = raw !== undefined && raw !== null ? (raw as number) : null;
      });
      return point;
    });
  }, [queries, block.segments, block.metric]);

  const series = block.segments.map((seg, i) => ({
    dataKey: seg.label,
    label: seg.label,
    color: PALETTE[i % PALETTE.length],
  }));

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity='error'>Couldn't load segment data.</Alert>;
  }

  if (chartData.length === 0) {
    return <Alert severity='info'>No data for this period.</Alert>;
  }

  const height = fill ? 320 : 260;

  return (
    <Box>
      {block.title && (
        <Typography variant='h6' fontWeight={600} gutterBottom>
          {block.title}
        </Typography>
      )}
      {block.chartKind === 'line' ? (
        <LineChart data={chartData} xAxisKey='label' series={series} height={height} />
      ) : (
        <VerticalBarChart
          data={chartData as Record<string, string | number>[]}
          xAxisKey='label'
          series={series}
          height={height}
        />
      )}
    </Box>
  );
};
